import { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { LessonCard } from '../components/LessonCard';
import { useQuery } from '@tanstack/react-query';
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";
import { BookOpen } from 'lucide-react';
import { motion } from 'framer-motion';

const Lessons: FC = () => {
  const navigate = useNavigate();

  // Obtener el estado de la suscripción
  const { data: subscription } = useQuery({
    queryKey: ['subscription'],
    queryFn: async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) return null;

      const { data, error } = await supabase
        .from('subscriptions')
        .select('*')
        .eq('user_id', session.user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching subscription:', error);
        return null;
      }

      return data;
    },
  });

  const hasValidSubscription = subscription && (
    subscription.status === 'active' || 
    (subscription.status === 'trial' && new Date(subscription.trial_ends_at) > new Date())
  );

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log('No active session found, redirecting to login');
        navigate('/login');
      }
    };
    
    checkAuth();
  }, [navigate]);

  const { data: lessons, isLoading, error } = useQuery({
    queryKey: ['lessons'],
    queryFn: async () => {
      console.log('Fetching lessons...');
      try {
        const { data, error } = await supabase
          .from('lessons')
          .select('*')
          .order('title', { ascending: true }); // Ordenar por título alfabéticamente
        
        if (error) {
          console.error('Error fetching lessons:', error);
          throw error;
        }
        
        console.log('Lessons fetched successfully:', data);
        return data;
      } catch (error) {
        console.error('Failed to fetch lessons:', error);
        toast({
          title: "Error",
          description: "No se pudieron cargar las lecciones. Por favor, intenta de nuevo.",
          variant: "destructive",
        });
        throw error;
      }
    },
    retry: 2,
    staleTime: 1000 * 60 * 5,
  });

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">
            <h2 className="text-xl font-bold text-red-600">
              Error al cargar las lecciones
            </h2>
            <p className="text-gray-600 mt-2">
              Por favor, intenta recargar la página
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
        <div className="flex items-center justify-center min-h-[calc(100vh-4rem)]">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-4 py-8">
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex items-center gap-3 mb-8"
        >
          <BookOpen className="w-8 h-8 text-primary animate-pulse" />
          <h1 className="text-3xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
            Lecciones Disponibles
          </h1>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {lessons?.map((lesson) => (
            <LessonCard
              key={lesson.id}
              id={lesson.id}
              title={lesson.title}
              description={lesson.content_text}
              points={100}
              isLocked={lesson.is_premium && !hasValidSubscription}
              videoUrl={lesson.video_url}
              isPremium={lesson.is_premium}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default Lessons;